import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, MagnifyingGlass, X } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sep 22 2026, user's explicit ask: let an admin permanently exempt a
// finished item from the by-product/Net Weight mandate at Production
// Confirmation, instead of chasing every new false-match keyword in
// code (see server.py's SCRAP_FAMILY_FALSE_MATCH_WORDS for the
// original WMNB51EBXL-16/"CUSHION" incident this replaces going
// forward). Scope is permanent + per finished item only, per user's
// explicit choice.
export default function ByproductExemptionsPage() {
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);
  const debounceRef = useRef(null);
  const requestRef = useRef(null);

  const loadExemptions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/byproduct-exemptions`);
      setExemptions(data);
    } catch (err) {
      toast.error("Could not load exemptions", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExemptions();
  }, [loadExemptions]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const requestId = q;
      requestRef.current = requestId;
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q, limit: 10 } });
        if (requestRef.current === requestId) setSuggestions(data);
      } catch {
        if (requestRef.current === requestId) setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const exemptedIds = new Set(exemptions.map((e) => e.product_id));

  const addExemption = async (item) => {
    setAdding(item.product_id);
    try {
      await axios.post(`${API}/admin/byproduct-exemptions/${encodeURIComponent(item.product_id)}`, { exempt: true });
      toast.success(`${item.product_id} exempted`, { description: "This item will never require a by-product at Production Confirmation." });
      setQuery("");
      setSuggestions([]);
      await loadExemptions();
    } catch (err) {
      toast.error("Could not add exemption", { description: err?.response?.data?.detail || err.message });
    } finally {
      setAdding(null);
    }
  };

  const removeExemption = async (productId) => {
    setRemoving(productId);
    try {
      await axios.post(`${API}/admin/byproduct-exemptions/${encodeURIComponent(productId)}`, { exempt: false });
      toast.success(`${productId} exemption removed`);
      await loadExemptions();
    } catch (err) {
      toast.error("Could not remove exemption", { description: err?.response?.data?.detail || err.message });
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <ShieldCheck size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="byproduct-exemptions-title">
            By-Product Exemptions
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Finished items marked here will never require a by-product/Net Weight before Production Confirmation
            can be posted - a permanent waiver, no code change needed.
          </p>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-6">
          <label className="text-xs font-medium text-[#667085] font-sans mb-1.5 block">Search finished item to exempt</label>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type Product ID or description (e.g. WMNB51EBXL-16)"
              className="pl-9 h-9"
              data-testid="byproduct-exemptions-search-input"
            />
          </div>
          {suggestions.length > 0 && (
            <div className="mt-2 border border-[#EAECF0] rounded-md divide-y divide-[#F2F4F7]" data-testid="byproduct-exemptions-suggestions">
              {suggestions.map((s) => (
                <div key={s.product_id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50" data-testid={`byproduct-exemptions-suggestion-${s.product_id}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold text-[#1D2939]">{s.product_id}</div>
                    {s.description && <div className="text-xs text-[#667085]">{s.description}</div>}
                  </div>
                  <Button
                    size="sm"
                    className="bg-[#0E7C86] hover:bg-[#0B6B74]"
                    disabled={exemptedIds.has(s.product_id) || adding === s.product_id}
                    onClick={() => addExemption(s)}
                    data-testid={`byproduct-exemptions-add-button-${s.product_id}`}
                  >
                    {exemptedIds.has(s.product_id) ? "Already exempted" : adding === s.product_id ? "Adding..." : "Add Exemption"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085] font-sans">
                <th className="px-3 py-2 font-medium">Product ID</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="byproduct-exemptions-loading">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && exemptions.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="byproduct-exemptions-empty">
                    No items exempted yet - search above to add one.
                  </td>
                </tr>
              )}
              {exemptions.map((e) => (
                <tr key={e.product_id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`byproduct-exemptions-row-${e.product_id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#1D2939]">{e.product_id}</td>
                  <td className="px-3 py-2 text-[#475467]">{e.description || "\u2014"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-[#B42318] border-[#FDA29B] hover:bg-[#FEF3F2]"
                      disabled={removing === e.product_id}
                      onClick={() => removeExemption(e.product_id)}
                      data-testid={`byproduct-exemptions-remove-button-${e.product_id}`}
                    >
                      <X size={13} weight="bold" />
                      {removing === e.product_id ? "Removing..." : "Remove"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
